import React, { useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "../../common/components/LocalizationProvider";
import LogoImage from "../../common/auth/LogoImage";
import { formatDate } from "../../common/util/formatter";
import "./printStyles.css";

interface PrintHeaderProps {
  title?: string;
  from?: string | Date;
  to?: string | Date;
  user?: string;
  device?: string;
}

const PrintHeader: React.FC<PrintHeaderProps> = ({ title, from, to, user, device }) => {
  const t = useTranslation();
  const theme = useTheme();

  const resolveDate = (value?: string | Date) => {
    if (!value) {
      return undefined;
    }
    const dateValue = value instanceof Date ? value : new Date(value);
    // preserved custom logic: mantemos o formatDate para respeitar timezone configurado.
    return formatDate(dateValue);
  };

  const headerTitle = title || t("reportTitle");
  const primaryColor = theme.palette.primary.main;
  const pdfColor = theme.palette.export?.pdf || primaryColor;

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = headerTitle;
    }
  }, [headerTitle]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }
    const root = document.documentElement;
    const previous = root.style.getPropertyValue("--color-primary");
    root.style.setProperty("--color-primary", pdfColor);
    return () => {
      if (previous) {
        root.style.setProperty("--color-primary", previous);
      } else {
        root.style.removeProperty("--color-primary");
      }
    };
  }, [pdfColor]);

  const headerStyle = useMemo(
    () => ({
      color: primaryColor,
      borderBottom: `3px solid ${primaryColor}`,
    }),
    [primaryColor],
  );

  return (
    <div className="print-header__container">
      <div className="print-header__row">
        <LogoImage
          forceLightTheme
          style={{ height: 60 }}
          alt="Logo"
          onLoad={() => {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                window.print();
              }),
            );
          }}
        />
        <h1 className="print-header__title" style={headerStyle}>{headerTitle}</h1>
      </div>
      <div className="print-header__meta">
        {user && (
          <div>
            <strong>{t("settingsUsers")}:</strong> {user}
          </div>
        )}
        {device && (
          <div>
            <strong>{t("sharedDevice")}:</strong> {device}
          </div>
        )}
        {from && (
          <div>
            <strong>{t("reportFrom")}:</strong> {resolveDate(from)}
          </div>
        )}
        {to && (
          <div>
            <strong>{t("reportTo")}:</strong> {resolveDate(to)}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintHeader;
